// src/ui/components/atelierTour.ts — Victorian Atelier Field Guide & Metrics Walkthrough (FR-TOUR-001, FR-TOUR-002)
// Provides a rich, 5-stage interactive walkthrough guiding users through the application's
// architecture, ADHD ergonomics, and Bexley Quest 11+ metrics (SAS Mean 200, 4-axis radar, fatigue curves).

import { renderCornerFiligree, renderCompassWatermark } from './ornateHelpers';

export interface TourStep {
  title: string;
  subtitle: string;
  badge: string;
  claweySay: string;
  renderContent: () => string;
}

export class AtelierTour {
  private container: HTMLElement;
  private currentStepIndex: number = 0;
  private steps: TourStep[] = [];
  private keydownHandler?: (e: KeyboardEvent) => void;
  private onFinished?: () => void;

  constructor(options: { onFinished?: () => void } = {}) {
    this.onFinished = options.onFinished;
    this.container = document.createElement('div');
    this.container.id = 'atelier-tour-modal';
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-label', 'Victorian Atelier Field Guide & Metrics Walkthrough');
    this.container.style.cssText = `
      position: fixed;
      inset: 0;
      background: radial-gradient(circle at center, rgba(10, 25, 47, 0.92), rgba(4, 10, 22, 0.98));
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      padding: 20px;
      animation: fadeIn 0.35s ease;
      touch-action: manipulation;
    `;

    this.initSteps();
    this.render();
    this.attachEvents();
  }

  static hasCompletedTour(): boolean {
    try {
      return localStorage.getItem('clawey_tour_completed') === 'true';
    } catch {
      return false;
    }
  }

  static markTourCompleted(): void {
    try {
      localStorage.setItem('clawey_tour_completed', 'true');
    } catch {
      // In constrained environments
    }
  }

  private initSteps(): void {
    this.steps = [
      // STEP 1: Atelier Philosophy & Clawey Mascot
      {
        title: 'Welcome to the Victorian Atelier',
        subtitle: 'A tactile, calm, distraction-free laboratory for the Bexley 11+',
        badge: 'STAGE 1 OF 5 · PHILOSOPHY',
        claweySay:
          'Ahoy, fellow naturalist! I am Clawey, your Socratic lobster mentor. Here, learning is treated as a grand marine expedition, never a high-pressure chore!',
        renderContent: () => `
          <div style="display: flex; flex-direction: column; gap: 16px; font-size: 0.95rem; line-height: 1.55; color: #2D3748;">
            <p>
              The <strong>Clawey 11+ Victorian Deep-Sea Atelier</strong> pairs authentic 19th-century naturalist aesthetics with state-of-the-art cognitive ergonomics tailored for Tudor's Year 4 learning journey.
            </p>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
              <div style="background: rgba(26, 54, 93, 0.04); border: 1px solid var(--vellum-edge); padding: 14px; border-radius: 10px;">
                <div style="font-weight: 700; color: var(--morris-indigo); display: flex; align-items: center; gap: 6px;">
                  <span>🧠</span> Socratic Dialogue
                </div>
                <div style="font-size: 0.85rem; margin-top: 4px; color: #4A5568;">
                  Clawey never delivers answers directly. He offers gentle, 2-to-3 sentence prompts that build self-directed deductive reasoning.
                </div>
              </div>
              <div style="background: rgba(26, 54, 93, 0.04); border: 1px solid var(--vellum-edge); padding: 14px; border-radius: 10px;">
                <div style="font-weight: 700; color: var(--morris-indigo); display: flex; align-items: center; gap: 6px;">
                  <span>🌊</span> Multi-Sensory Tactile Feel
                </div>
                <div style="font-size: 0.85rem; margin-top: 4px; color: #4A5568;">
                  3D WebGL marine specimens, drag-to-measure protractors, and physical OMR bubble sheets replicate exam-day reality.
                </div>
              </div>
            </div>
          </div>
        `,
      },

      // STEP 2: Stations & ADHD Ergonomics
      {
        title: 'Learning Stations & ADHD Focus',
        subtitle: 'Bite-sized sprints, single-question screens, and forgiving streaks',
        badge: 'STAGE 2 OF 5 · STATIONS & ADHD',
        claweySay:
          'When questions appear, all distractions fade away! No sidebars or countdown panic — just one challenge at a time.',
        renderContent: () => `
          <div style="display: flex; flex-direction: column; gap: 14px; font-size: 0.92rem; line-height: 1.5; color: #2D3748;">
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px;">
              <div style="border-left: 3px solid var(--deep-coral); padding-left: 10px;">
                <strong style="color: var(--morris-indigo);">📖 Clue Hunter &amp; Context Lens</strong>
                <p style="margin: 2px 0 0 0; font-size: 0.82rem; color: #4A5568;">
                  KS2 Reading Comprehension (Domain 2d/2b) with &le;60-word passage cards and tap-to-highlight textual proof.
                </p>
              </div>
              <div style="border-left: 3px solid var(--gilded-brass); padding-left: 10px;">
                <strong style="color: var(--morris-indigo);">⚡ 10-Min Quest Sprints</strong>
                <p style="margin: 2px 0 0 0; font-size: 0.82rem; color: #4A5568;">
                  Rapid-fire Verbal, Non-Verbal, English, and Maths drills using visual disc timers and Zen Mode.
                </p>
              </div>
              <div style="border-left: 3px solid #0284C7; padding-left: 10px;">
                <strong style="color: var(--morris-indigo);">📐 Tactile Geometry</strong>
                <p style="margin: 2px 0 0 0; font-size: 0.82rem; color: #4A5568;">
                  Interactive SVG Claw Protractor and mirror reflection line calibrated for tablet touch manipulation.
                </p>
              </div>
              <div style="border-left: 3px solid #1E5A3C; padding-left: 10px;">
                <strong style="color: var(--morris-indigo);">📷 Schoolwork Scanner</strong>
                <p style="margin: 2px 0 0 0; font-size: 0.82rem; color: #4A5568;">
                  Snap photos of Tudor's school worksheets for AI error-gap diagnosis and 5 custom remediation questions.
                </p>
              </div>
            </div>

            <div style="background: rgba(229, 169, 60, 0.12); border: 1px solid var(--gilded-brass); padding: 12px 16px; border-radius: 10px;">
              <strong style="color: var(--morris-indigo);">🎯 ADHD Guardrails:</strong>
              <ul style="margin: 6px 0 0 0; padding-left: 20px; font-size: 0.84rem; color: #333;">
                <li><strong>Single-Question Screens:</strong> Auto-hides sidebar and top-nav while answering to prevent sensory overload.</li>
                <li><strong>20–30 Min Practice Cap:</strong> Daily practice guard prevents cognitive exhaustion.</li>
                <li><strong>Forgiving Streaks:</strong> Pauses for 48 hours without penalizing missed days (never shame-driven).</li>
              </ul>
            </div>
          </div>
        `,
      },

      // STEP 3: Bexley Quest 11+ Mock Exam & SAS Mean 200
      {
        title: 'Bexley Quest 11+ & The SAS 200 Scale',
        subtitle: 'Understanding Standardised Age Scores (SAS) and the Bexley Selection format',
        badge: 'STAGE 3 OF 5 · SCORING EXPLAINED',
        claweySay:
          'A score of 80% on a hard test can be far superior to 90% on an easy one! Standardised Age Scores ensure fairness for every month of birth.',
        renderContent: () => `
          <div style="display: flex; flex-direction: column; gap: 14px; font-size: 0.92rem; line-height: 1.5; color: #2D3748;">
            <p>
              The Bexley Selection Test is produced by <strong>Quest Assessments (Atom Learning)</strong>. It consists of two ~50-minute multiple-choice papers with an OMR answer sheet:
            </p>

            <div style="background: rgba(26, 54, 93, 0.05); border-radius: 10px; padding: 12px 16px; border: 1px solid var(--vellum-edge);">
              <div style="font-weight: 700; color: var(--morris-indigo); margin-bottom: 6px;">
                📊 The Quest Standardised Age Score (SAS) Model:
              </div>
              <ul style="margin: 0; padding-left: 20px; font-size: 0.85rem; color: #4A5568;">
                <li><strong>Paper 1 (Verbal Ability &amp; Reading Comprehension):</strong> Standardized to <strong>Mean 100</strong> (SD 15).</li>
                <li><strong>Paper 2 (Numerical Reasoning &amp; Non-Verbal / Spatial):</strong> Standardized to <strong>Mean 100</strong> (SD 15).</li>
                <li><strong>Combined Aggregate SAS:</strong> Paper 1 + Paper 2 = <strong>National Mean 200</strong>.</li>
                <li><strong>Age Normalisation:</strong> Calibrated specifically to Tudor's exact birth month (Jan 2018 cohort), ensuring younger pupils in the school year are not disadvantaged.</li>
              </ul>
            </div>

            <div style="display: flex; gap: 12px; align-items: center; background: #FFFDF9; border: 1px solid var(--gilded-brass); border-radius: 10px; padding: 12px 14px;">
              <span style="font-size: 1.6rem;">🏛️</span>
              <div style="font-size: 0.85rem; color: #2D3748;">
                <strong>Bexley Qualifying Benchmark: ~216 Aggregate SAS</strong> (historically top 25–28% of applicants). The top 180 guaranteed grammar places typically require ~240+.
              </div>
            </div>
          </div>
        `,
      },

      // STEP 4: Parent Deck Analytics & Fatigue Curve
      {
        title: 'Deep Analytics: Radar & Fatigue Curve',
        subtitle: 'Pinpointing stamina drop-offs and distinguishing impulsive rushing from conceptual gaps',
        badge: 'STAGE 4 OF 5 · ANALYTICS',
        claweySay:
          'Watch Tudor’s stamina curve! If accuracy drops in minutes 21–30, it tells us Tudor needs stamina pacing, not harder worksheets.',
        renderContent: () => `
          <div style="display: flex; flex-direction: column; gap: 14px; font-size: 0.92rem; line-height: 1.5; color: #2D3748;">
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px;">
              <div style="background: rgba(26, 54, 93, 0.04); border: 1px solid var(--vellum-edge); padding: 12px; border-radius: 10px;">
                <div style="font-weight: 700; color: var(--morris-indigo);">🧭 4-Axis Bexley Readiness Radar</div>
                <p style="font-size: 0.83rem; margin: 4px 0 0 0; color: #4A5568;">
                  Displays performance across <strong>Verbal, Comprehension, Numerical, and Spatial</strong> domains relative to benchmark:
                </p>
                <div style="margin-top: 6px; font-size: 0.78rem; display: flex; flex-direction: column; gap: 3px;">
                  <span style="color: #1E5A3C; font-weight: 700;">● Ahead (SAS &ge; 115)</span>
                  <span style="color: #0369A1; font-weight: 700;">● On Track (SAS 105–114)</span>
                  <span style="color: #C53030; font-weight: 700;">● Support Needed (SAS &lt; 105)</span>
                </div>
              </div>

              <div style="background: rgba(26, 54, 93, 0.04); border: 1px solid var(--vellum-edge); padding: 12px; border-radius: 10px;">
                <div style="font-weight: 700; color: var(--morris-indigo);">📉 10-Minute Fatigue Decay Curve</div>
                <p style="font-size: 0.83rem; margin: 4px 0 0 0; color: #4A5568;">
                  Segments accuracy across three blocks:
                </p>
                <div style="margin-top: 6px; font-size: 0.78rem; display: flex; flex-direction: column; gap: 3px;">
                  <span><strong>Mins 1–10:</strong> Baseline focus &amp; freshness</span>
                  <span><strong>Mins 11–20:</strong> Sustained cognitive output</span>
                  <span><strong>Mins 21–30:</strong> Stamina fatigue threshold</span>
                </div>
              </div>
            </div>

            <div style="background: rgba(255, 107, 74, 0.08); border-left: 3px solid var(--deep-coral); padding: 10px 14px; border-radius: 6px; font-size: 0.84rem; color: #2D3748;">
              ⚡ <strong>Impulsive-Guess Detection (&lt;1.5s):</strong> The telemetry collector tags questions submitted in under 1500ms. This prevents misdiagnosing impulsive ADHD clicking as a subject-matter deficit.
            </div>
          </div>
        `,
      },

      // STEP 5: Tester Sandbox Profile & Safe Exploration
      {
        title: 'Tester Sandbox: Test Run With Confidence',
        subtitle: 'Experiment freely without polluting Tudor’s longitudinal records',
        badge: 'STAGE 5 OF 5 · TESTER SANDBOX',
        claweySay:
          'Feel free to test every corner of the laboratory yourself! Switch to the Tester Sandbox profile at any time.',
        renderContent: () => `
          <div style="display: flex; flex-direction: column; gap: 14px; font-size: 0.95rem; line-height: 1.55; color: #2D3748;">
            <p>
              We know you want to experience the sprint timers, solve comprehension clues, and take mock exams yourself.
            </p>

            <div style="display: flex; gap: 14px; background: #FFFDF9; border: 2px solid #0284C7; border-radius: 12px; padding: 16px;">
              <span style="font-size: 2rem;">🧪</span>
              <div>
                <div style="font-weight: 700; color: #0369A1; font-size: 1.05rem;">
                  Isolated Storage Namespace
                </div>
                <div style="font-size: 0.88rem; color: #4A5568; margin-top: 4px;">
                  When in <strong>Tester Sandbox</strong> mode, all scores, mock submissions, streaks, and telemetry are isolated. Tudor's genuine stats remain 100% clean.
                </div>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 0.85rem;">
              <div style="background: rgba(26, 54, 93, 0.04); padding: 12px; border-radius: 8px;">
                <strong>1. Switch Profiles:</strong>
                <p style="margin: 4px 0 0 0; color: #555;">Click the profile pill in the top header bar to toggle between Tudor and Tester.</p>
              </div>
              <div style="background: rgba(26, 54, 93, 0.04); padding: 12px; border-radius: 8px;">
                <strong>2. Reset Anytime:</strong>
                <p style="margin: 4px 0 0 0; color: #555;">Click "Purge Sandbox Data" in the profile modal to wipe test records instantly.</p>
              </div>
            </div>
          </div>
        `,
      },
    ];
  }

  show(): void {
    document.body.appendChild(this.container);
  }

  close(): void {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
    }
    AtelierTour.markTourCompleted();
    this.container.remove();
    this.onFinished?.();
  }

  getCurrentStepIndex(): number {
    return this.currentStepIndex;
  }

  goToStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.currentStepIndex = index;
      this.render();
      this.attachEvents();
    }
  }

  private render(): void {
    const step = this.steps[this.currentStepIndex];
    if (!step) return;
    const isFirst = this.currentStepIndex === 0;
    const isLast = this.currentStepIndex === this.steps.length - 1;

    this.container.innerHTML = `
      <div class="vellum-surface" style="
        width: 100%;
        max-width: 680px;
        position: relative;
        padding: 32px 30px;
        border-radius: 20px;
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55), inset 0 0 50px rgba(229, 169, 60, 0.1);
      ">
        ${renderCornerFiligree()}
        ${renderCompassWatermark()}

        <!-- Top Header & Step Badge -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; position: relative; z-index: 2;">
          <div>
            <div class="atelier-eyebrow-pill">
              <span>🧭</span>
              <span id="tour-step-badge">${step.badge}</span>
            </div>
            <h2 id="tour-step-title" style="font-family: var(--font-heading); font-size: 1.5rem; color: var(--morris-indigo); margin: 6px 0 2px 0;">
              ${step.title}
            </h2>
            <div style="font-size: 0.85rem; color: #5A6A80; font-weight: 600;">
              ${step.subtitle}
            </div>
          </div>
          <button id="tour-close-btn" class="touch-target btn-vellum" style="min-width: 44px; width: 44px; height: 44px; border-radius: 50%; padding: 0; font-size: 1.25rem; display: flex; align-items: center; justify-content: center; cursor: pointer;" aria-label="Close Field Guide Tour">
            &times;
          </button>
        </div>

        <!-- Clawey Mascot Commentary Bubble -->
        <div style="
          display: flex;
          gap: 12px;
          align-items: center;
          background: linear-gradient(135deg, rgba(255, 253, 249, 0.95), rgba(245, 235, 206, 0.95));
          border: 1px solid var(--gilded-brass);
          border-radius: 12px;
          padding: 10px 14px;
          margin-bottom: 20px;
          position: relative;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(229, 169, 60, 0.15);
        ">
          <img src="/assets/svg/clawey-icon-192.svg" alt="Clawey" style="width: 40px; height: 40px; flex-shrink: 0;" />
          <div style="font-size: 0.85rem; line-height: 1.4; color: var(--morris-indigo); font-style: italic;">
            "${step.claweySay}"
          </div>
        </div>

        <!-- Step Specific Content Body -->
        <div id="tour-step-content" style="min-height: 200px; position: relative; z-index: 2;">
          ${step.renderContent()}
        </div>

        <!-- Navigation Row & Step Dots -->
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 18px;
          border-top: 1px solid rgba(213, 197, 163, 0.7);
          position: relative;
          z-index: 2;
        ">
          <!-- Step Dots -->
          <div style="display: flex; gap: 8px;" aria-label="Tour Progress">
            ${this.steps
              .map(
                (_, idx) => `
                <button class="tour-dot-btn" data-step="${idx}" style="
                  width: ${idx === this.currentStepIndex ? '24px' : '10px'};
                  height: 10px;
                  border-radius: 5px;
                  border: none;
                  background: ${idx === this.currentStepIndex ? 'var(--deep-coral)' : 'var(--vellum-edge)'};
                  cursor: pointer;
                  padding: 0;
                  transition: all 0.25s ease;
                " aria-label="Go to stage ${idx + 1}"></button>
              `
              )
              .join('')}
          </div>

          <!-- Controls (Prev / Next / Finish) -->
          <div style="display: flex; gap: 10px;">
            <button id="tour-prev-btn" class="touch-target btn-vellum" style="min-height: 44px; padding: 6px 16px; font-size: 0.9rem;" ${isFirst ? 'disabled' : ''}>
              &larr; Previous
            </button>
            <button id="tour-next-btn" class="touch-target ${isLast ? 'btn-brass' : 'btn-coral'}" style="min-height: 44px; padding: 6px 20px; font-size: 0.9rem; font-weight: 700;">
              ${isLast ? 'Explore Atelier 🌊' : 'Next &rarr;'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    // Close button
    this.container.querySelector('#tour-close-btn')?.addEventListener('click', () => {
      this.close();
    });

    // Close on backdrop click
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    // Prev button
    this.container.querySelector('#tour-prev-btn')?.addEventListener('click', () => {
      if (this.currentStepIndex > 0) {
        this.goToStep(this.currentStepIndex - 1);
      }
    });

    // Next / Finish button
    this.container.querySelector('#tour-next-btn')?.addEventListener('click', () => {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.goToStep(this.currentStepIndex + 1);
      } else {
        this.close();
      }
    });

    // Dot navigation
    const dots = this.container.querySelectorAll<HTMLButtonElement>('.tour-dot-btn');
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const stepIdx = parseInt(dot.getAttribute('data-step') ?? '0', 10);
        this.goToStep(stepIdx);
      });
    });

    // Keyboard navigation (Esc, Left arrow, Right arrow)
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowRight' && this.currentStepIndex < this.steps.length - 1) {
        this.goToStep(this.currentStepIndex + 1);
      } else if (e.key === 'ArrowLeft' && this.currentStepIndex > 0) {
        this.goToStep(this.currentStepIndex - 1);
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }
}
